import React, { useCallback, useState } from 'react';
import { Pressable, LayoutChangeEvent } from 'react-native';
import styled from '@emotion/native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  interpolate,
  Easing,
} from 'react-native-reanimated';
import { BodyText } from '@/shared/components/StyledText';
import { green } from '@/shared/styles/colors';

interface PricingAccordionProps {
  virtualRate: string;
  inPersonRate: string;
}

const ANIMATION_DURATION = 250;

const PricingAccordion: React.FC<PricingAccordionProps> = ({
  virtualRate,
  inPersonRate,
}) => {
  const [contentHeight, setContentHeight] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const expanded = useSharedValue(0);

  const toggleExpanded = useCallback(() => {
    const willExpand = expanded.value === 0;
    setIsExpanded(willExpand);
    expanded.value = withTiming(willExpand ? 1 : 0, {
      duration: ANIMATION_DURATION,
      easing: Easing.bezier(0.25, 0.1, 0.25, 1),
    });
  }, [expanded]);

  const handleContentLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { height } = event.nativeEvent.layout;
      if (height > 0 && contentHeight === 0) {
        setContentHeight(height);
      }
    },
    [contentHeight],
  );

  const containerAnimatedStyle = useAnimatedStyle(() => {
    return {
      height: interpolate(expanded.value, [0, 1], [0, contentHeight]),
      opacity: expanded.value,
    };
  });

  const chevronAnimatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        { rotate: `${interpolate(expanded.value, [0, 1], [0, 180])}deg` },
      ],
    };
  });

  const labelAnimatedStyle = useAnimatedStyle(() => {
    return {
      opacity: interpolate(expanded.value, [0, 0.5], [1, 0]),
    };
  });

  const hideLabelAnimatedStyle = useAnimatedStyle(() => {
    return {
      opacity: interpolate(expanded.value, [0.5, 1], [0, 1]),
    };
  });

  return (
    <Container>
      <Toggle
        onPress={toggleExpanded}
        accessibilityRole="button"
        accessibilityState={{ expanded: isExpanded }}
        accessibilityLabel="Pricing details"
        accessibilityHint="Double tap to expand or collapse pricing options"
      >
        <LabelContainer>
          <AnimatedLabelWrapper style={labelAnimatedStyle}>
            <LabelText>View Pricing</LabelText>
          </AnimatedLabelWrapper>
          <AnimatedHideLabelWrapper style={hideLabelAnimatedStyle}>
            <LabelText>Hide Pricing</LabelText>
          </AnimatedHideLabelWrapper>
        </LabelContainer>
        <AnimatedChevron style={chevronAnimatedStyle}>
          <ChevronImage
            source={require('@/assets/images/Chevron.png')}
            tintColor={green}
          />
        </AnimatedChevron>
      </Toggle>
      <AnimatedDetailsWrapper style={containerAnimatedStyle}>
        <DetailsContainer onLayout={handleContentLayout}>
          <DetailText>Virtual: {virtualRate}</DetailText>
          <DetailText>In-Person: {inPersonRate}</DetailText>
        </DetailsContainer>
      </AnimatedDetailsWrapper>
      {/* Hidden content to measure height */}
      {contentHeight === 0 && (
        <MeasureContainer onLayout={handleContentLayout}>
          <DetailText>Virtual: {virtualRate}</DetailText>
          <DetailText>In-Person: {inPersonRate}</DetailText>
        </MeasureContainer>
      )}
    </Container>
  );
};

export default PricingAccordion;

const Container = styled.View({
  flexDirection: 'column',
});

const ChevronImage = styled.Image({
  width: 10,
  tintColor: green,
  resizeMode: 'contain',
});

const Toggle = styled(Pressable)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 6,
});

const LabelContainer = styled.View({
  position: 'relative',
});

const AnimatedLabelWrapper = styled(Animated.View)({});

const AnimatedHideLabelWrapper = styled(Animated.View)({
  position: 'absolute',
  top: 0,
  left: 0,
});

const LabelText = styled(BodyText)({
  fontSize: 16,
  fontWeight: 'bold',
  color: green,
});

const AnimatedChevron = styled(Animated.View)({
  justifyContent: 'center',
  alignItems: 'center',
});

const AnimatedDetailsWrapper = styled(Animated.View)({
  overflow: 'hidden',
});

const DetailsContainer = styled.View({
  paddingTop: 8,
});

const MeasureContainer = styled.View({
  position: 'absolute',
  opacity: 0,
  pointerEvents: 'none',
  paddingTop: 8,
});

const DetailText = styled(BodyText)({
  fontSize: 15,
  color: green,
  lineHeight: 20,
  fontWeight: 'normal',
});
